import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  ReactElement,
} from "react";
import { useError } from "./ErrorContext";
import {
  getStandards,
  updateIncompleteStandards,
} from "../services/standards-service";
import { useUserContext } from "./user-context";
import { IStandards } from "../types/standard";
import { AppError } from "../types/app";

const StandardsContext = createContext<StandardsProviderType>({
  submittedStandards: [],
  unsubmittedStandards: [],
  refreshData: () => undefined,
});

type StandardsType = {
  submittedStandards: IStandards[];
  unsubmittedStandards: IStandards[];
};

type StandardsProviderType = {
  submittedStandards: IStandards[];
  unsubmittedStandards: IStandards[];
  refreshData: () => void;
};

export const StandardsProvider = ({ children }: { children: ReactElement }) => {
  const { user } = useUserContext();
  const [standards, setStandards] = useState<StandardsType>({
    submittedStandards: [],
    unsubmittedStandards: [],
  });
  const [isFetching, setIsFetching] = useState(false);
  const [hasFetchedData, setHasFetchedData] = useState(false);
  const { handleError } = useError();

  const getAllStandards = React.useCallback(async () => {
    setIsFetching(true);
    try {
      const incompleteAssessments: IStandards[] = [];
      const completedAssessments: IStandards[] = [];
      const modules = await getStandards();
      modules.forEach((eachModule) => {
        if (eachModule.submittedBy !== undefined && eachModule.submittedAt) {
          completedAssessments.push(eachModule);
        } else {
          incompleteAssessments.push(eachModule);
        }
      });
      setStandards({
        submittedStandards: completedAssessments,
        unsubmittedStandards: incompleteAssessments,
      });
      if (incompleteAssessments) {
        await updateIncompleteStandards();
      }
      setIsFetching(false);
      setHasFetchedData(true);
    } catch (e) {
      const error = e as AppError;
      if (error && error.message) {
        handleError(error.message);
      }
      setIsFetching(false);
      setHasFetchedData(true);
    }
  }, [handleError]);

  useEffect(() => {
    if (!isFetching && !hasFetchedData && user?.org) {
      getAllStandards();
    }
  }, [getAllStandards, isFetching, hasFetchedData, user?.org]);

  return (
    <StandardsContext.Provider
      value={{ ...standards, refreshData: getAllStandards }}
    >
      {children}
    </StandardsContext.Provider>
  );
};

export const useStandards = () => useContext(StandardsContext);
